import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, ListItemIcon, MenuItem, Tooltip } from '@mui/material';
import { AccountBalanceWallet, AddCircleOutline, Delete, DeleteOutline, ReceiptOutlined, Refresh } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { toast } from 'react-toastify';
import TableView from '../../components/TableView';
import ConfirmDialog from '../../components/ConfirmDialog';
import ExportMenuButton from '../../components/ExportMenuButton';
import { useAuth } from '../../hooks/useAuth';
import { useUI } from '../../providers/MoleUIContext';
import { useData } from '../../hooks/useData';
import { periodRequestConfig } from '../../hooks/useDateRange';
import { Util } from '../../util/Util';
import { LocalStorageKeys } from '../../util/constants';
import type { ClientWalletRow, WalletRecordRow } from './walletTypes';
import { isReceiptableWalletRecord, isTransferTag, walletSignedAmount } from './walletTypes';
import { walletRecordColumns } from './walletColumns';
import { useWalletRecordDelete } from './useWalletRecordDelete';
import { useWalletDirectory } from './useWalletDirectory';
import WalletClientModal from './WalletClientModal';
import WalletRecordModal from './WalletRecordModal';
import ReceiptDialog from '../../components/ReceiptDialog';
import type { ReceiptSource } from '../../util/receiptPdf';
import { canPrintReceipt, canRecord } from '../../util/access';

const Wallet = () => {
  const { api, access } = useAuth();
  const { queueReRun, t } = useUI();
  const {
    data,
    lastUpdate,
    loadFromIndexedDB,
    saveFromServer,
    dateRange: period,
    setDateRange: setPeriod,
    loading,
    setLoading,
  } = useData(LocalStorageKeys.WALLET_RECORDS);
  const { wallets, fetchWallets, setWallets } = useWalletDirectory();
  const [selected, setSelected] = useState<ClientWalletRow | null>(null);
  const [postWallet, setPostWallet] = useState<ClientWalletRow | null>(null);
  const [postOpen, setPostOpen] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);
  const firstFetchRef = useRef(false);
  const canPost = canRecord(access, 'wallet');
  const mayPrintReceipt = canPrintReceipt(access, 'read');
  const [receiptSource, setReceiptSource] = useState<ReceiptSource | null>(null);
  const toastIdRef = useRef<string | number>(-1);

  const fetchData = async () => {
    setLoading(true);
    const res = await api.get('/wallets/records', periodRequestConfig(period));
    if (res?.status === 200) {
      const rows = Array.isArray(res.data) ? res.data : res.data?.items || [];
      saveFromServer(rows, period);
      toast.update(toastIdRef.current, {
        render: t('common:toast.success.refresh'),
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
    } else if (res?.status === 401 || res?.status === 422) {
      queueReRun(fetchData);
    } else {
      toast.dismiss(toastIdRef.current);
    }
    setLoading(false);
  };

  const reloadRecords = () => {
    void fetchData();
    void fetchWallets(false);
    setRefreshToken((n) => n + 1);
  };

  const {
    canDelete,
    canDeleteBulk,
    toDelete,
    toDeleteBulk,
    setToDelete,
    setToDeleteBulk,
    handleDelete,
    handleBulkDelete,
    requestRowDelete,
    requestSelectionDelete,
    isConfirmOpen,
  } = useWalletRecordDelete(reloadRecords, toastIdRef);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const loaded = await loadFromIndexedDB();
      if (cancelled || firstFetchRef.current) return;
      firstFetchRef.current = true;
      if (!loaded) void fetchData();
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const walletForRow = (row: WalletRecordRow | ClientWalletRow): ClientWalletRow => {
    const clientId = 'client_id' in row ? row.client_id : row.client;
    const name = 'client_name' in row ? row.client_name : row.client;
    return wallets.find((wallet) => wallet.client_id === clientId) ?? {
      client_id: clientId,
      client: name,
      balance: 0,
      is_active: true,
    };
  };

  const openClient = (row: WalletRecordRow | ClientWalletRow) => {
    setSelected(walletForRow(row));
  };

  const columns = useMemo(
    () => walletRecordColumns(t, { onClientClick: openClient }),
    [t, wallets],
  );

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <TableView
				stateKey={LocalStorageKeys.WALLET_RECORDS}
				posting="wallet"
          dataScope="wallet_record"
          header={t('common:wallet.movements')}
          data={data}
          columns={columns}
          lastUpdated={lastUpdate}
          period={period}
          setDateRangeFilter={setPeriod}
          isLoading={loading}
          totals={[{ label: t('common:tables.amount'), accessorFn: (row) => walletSignedAmount(row) }]}
          renderTopToolbarActions={(table) => (
            <Box sx={{ display: 'flex', gap: '0.5rem' }}>
              <Tooltip title={t('common:ui.tooltips.refreshData')}>
                <IconButton onClick={() => { void fetchData(); void fetchWallets(false); }} disabled={loading} color="primary">
                  <Refresh />
                </IconButton>
              </Tooltip>
              {canPost && (
                <Tooltip title={t('common:wallet.postMovement')}>
                  <IconButton
                    color="success"
                    onClick={() => {
                      setPostWallet(null);
                      setPostOpen(true);
                    }}
                  >
                    <AddCircleOutline />
                  </IconButton>
                </Tooltip>
              )}
              {canDeleteBulk && (table.getIsSomeRowsSelected() || table.getIsAllRowsSelected()) && (
                <Tooltip title={t('common:ui.tooltips.deleteSelection')}>
                  <IconButton color="error" onClick={() => requestSelectionDelete(table)}>
                    <Delete />
                  </IconButton>
                </Tooltip>
              )}
              <ExportMenuButton
                prefix="wallet-transactions"
                data={data}
                columns={columns}
                table={table}
              />
            </Box>
          )}
          renderRowActionMenuItems={(row, closeMenu) => [
            <MenuItem
              key="open"
              onClick={() => {
                openClient(row.original);
                closeMenu();
              }}
            >
              <ListItemIcon><AccountBalanceWallet fontSize="small" /></ListItemIcon>
              {t('common:wallet.viewRecords')}
            </MenuItem>,
            ...(canPost ? [
              <MenuItem
                key="post"
                onClick={() => {
                  setPostWallet(walletForRow(row.original));
                  setPostOpen(true);
                  closeMenu();
                }}
              >
                <ListItemIcon><AddCircleOutline fontSize="small" /></ListItemIcon>
                {t('common:wallet.postMovement')}
              </MenuItem>,
            ] : []),
            ...(mayPrintReceipt && isReceiptableWalletRecord(row.original) ? [
              <MenuItem
                key="receipt"
                onClick={() => {
                  setReceiptSource({ kind: 'wallet', id: Number(row.original.id) });
                  closeMenu();
                }}
              >
                <ListItemIcon><ReceiptOutlined fontSize="small" /></ListItemIcon>
                {t('common:receipt.action')}
              </MenuItem>,
            ] : []),
            ...(canDelete ? [
              <MenuItem
                key="delete"
                onClick={() => {
                  requestRowDelete(row.original);
                  closeMenu();
                }}
                sx={{ color: 'error.main' }}
              >
                <ListItemIcon sx={{ color: 'error.main' }}><Delete fontSize="small" /></ListItemIcon>
                {t('common:ui.buttons.delete')}
              </MenuItem>,
            ] : []),
          ]}
        />
      </LocalizationProvider>

      <WalletClientModal
        isOpen={!!selected}
        wallet={selected}
        onClose={() => setSelected(null)}
        onPost={() => {
          setPostWallet(selected);
          setPostOpen(true);
        }}
        onChanged={reloadRecords}
        refreshToken={refreshToken}
      />

      <WalletRecordModal
        isOpen={postOpen}
        wallet={postWallet}
        wallets={wallets}
        onWalletsChange={setWallets}
        onClose={() => setPostOpen(false)}
        onSuccess={reloadRecords}
      />

      <ReceiptDialog source={receiptSource} onClose={() => setReceiptSource(null)} />

      <ConfirmDialog
        onConfirm={toDelete ? handleDelete : handleBulkDelete}
        onCancel={() => { toDelete ? setToDelete(null) : setToDeleteBulk([]); }}
        isOpen={isConfirmOpen}
        setIsOpen={() => {}}
        title={<><DeleteOutline sx={{ color: 'red' }} /> {t('common:dialog.deleteConfirmation')}</>}
        message={
          toDelete ? (
            <>
              {t('common:wallet.confirm.deleteSingleAmount')}&nbsp;
              <b><u>{Util.formatNumber(toDelete.amount)}</u></b>&nbsp;
              {t('common:wallet.confirm.deleteSingleDate')} <b><u>{Util.formatDate(toDelete.date, 'full')}</u></b>&nbsp;
              {t('common:wallet.confirm.deleteSingleClient')} <b><u>{toDelete.client_name}</u></b>!
              {isTransferTag(toDelete.tag) && (
                <>
                  <br />
                  {t('common:wallet.confirm.deleteTransferNote')}
                </>
              )}
            </>
          ) : (
            <>{t('common:wallet.confirm.deleteBulk', { count: Number(toDeleteBulk.length) })}</>
          )
        }
        confirmBtnColor="error"
        confirmBtnText={t('common:ui.buttons.delete')}
      />
    </>
  );
};

export default Wallet;
